import { useCallback, useEffect, useRef, useState } from 'react';
import { useShellAppContext } from '../../../shell/context/useShellAppContext';
import {
  notifyOperatorStateRefresh,
  notifyRuntimeOperationState,
} from '../../livePublishEvents';
import { ADMIN_RUNTIME_COMMANDS } from '../../../../app/runtime/adminRuntime';
import { useLocalizedMessage } from '../../../../util/useLocalizedMessage';

function isPending(status) {
  return status === 'queued' || status === 'running' || status === 'accepted';
}

function getErrorMessage(error, fallback) {
  if (!error) return fallback;
  if (typeof error === 'string') return error;
  return error.message || error.error || fallback;
}

/**
 * UI state adapter for product-neutral bundle runtime operations.
 *
 * Returns:
 *   status               — null | 'queued' | 'running' | 'success' | 'stale' | 'failed'
 *   error                — string | null
 *   startPreviewBuild    — fn(shopId: string) → void
 *   startBuildAndPromote — fn() → void
 */
export function useBundleRebuild(overrides = {}) {
  const t = useLocalizedMessage();
  const shellContext = useShellAppContext();
  const runtime = overrides.runtime || shellContext.runtime;
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [operation, setOperation] = useState(null);

  const cancelledRef = useRef(false);
  const operationIdRef = useRef(0);

  useEffect(() => () => {
    cancelledRef.current = true;
    operationIdRef.current += 1;
  }, []);

  const startOperation = useCallback(async (operationType, label, command, shopId) => {
    if (isPending(status)) return { success: false, error: 'operation_pending' };
    if (typeof runtime?.supports === 'function' && !runtime.supports(command)) {
      return {
        success: false,
        error: t('operator.bundle.updatePluginRequired'),
      };
    }
    const operationId = operationIdRef.current + 1;
    operationIdRef.current = operationId;
    cancelledRef.current = false;
    setOperation(operationType);
    setStatus('queued');
    setError(null);
    notifyRuntimeOperationState({ operation: operationType, status: 'queued', error: null });

    try {
      if (!runtime || typeof runtime.sendMessageWithResponse !== 'function') {
        throw new Error(t('operator.bundle.runtimeUnavailable'));
      }
      setStatus('running');
      notifyRuntimeOperationState({ operation: operationType, status: 'running', error: null });
      const response = await runtime.sendMessageWithResponse(command, { shopId });
      if (cancelledRef.current || operationIdRef.current !== operationId) {
        return { success: false, error: 'cancelled' };
      }
      if (response?.success === false) {
        throw new Error(response.error || `${label} failed`);
      }
      if (response?.status === 'stale' || response?.publishedClean === false) {
        const message = response.verificationError
          || t('operator.bundle.newerDraftPending');
        notifyOperatorStateRefresh();
        setStatus('stale');
        setError(message);
        notifyRuntimeOperationState({
          operation: operationType,
          status: 'stale',
          error: message,
        });
        return { success: true, stale: true, details: response };
      }
      notifyOperatorStateRefresh();
      setStatus('success');
      notifyRuntimeOperationState({ operation: operationType, status: 'success', error: null });
      return { success: true, details: response };
    } catch (err) {
      const message = getErrorMessage(err, `${label} failed`);
      if (!cancelledRef.current && operationIdRef.current === operationId) {
        setStatus('failed');
        setError(message);
        notifyRuntimeOperationState({ operation: operationType, status: 'failed', error: message });
        notifyOperatorStateRefresh();
      }
      return { success: false, error: message };
    }
  }, [runtime, status, t]);

  const startPreviewBuild = useCallback((shopId) => {
    return startOperation(
      'preview',
      t('operator.bundle.previewBuild'),
      ADMIN_RUNTIME_COMMANDS.BUNDLE_BUILD_PREVIEW,
      shopId,
    );
  }, [startOperation, t]);

  const startBuildAndPromote = useCallback((shopId) => {
    return startOperation(
      'production',
      t('operator.bundle.buildAndPromote'),
      ADMIN_RUNTIME_COMMANDS.BUNDLE_BUILD_AND_PROMOTE,
      shopId,
    );
  }, [startOperation, t]);

  const reset = useCallback(() => {
    operationIdRef.current += 1;
    setStatus(null);
    setError(null);
    setOperation(null);
    notifyRuntimeOperationState({ operation: null, status: null, error: null });
  }, []);

  return {
    status,
    error,
    operation,
    startPreviewBuild,
    startBuildAndPromote,
    reset,
  };
}
